import type { NormalizedSession, RegistryFeature, TagResult } from "./types.js";

/**
 * Auto-tag a session to a feature using a 4-signal cascade.
 * Cheapest signals first, compound for higher confidence.
 */
export function autoTag(
  session: NormalizedSession,
  features: RegistryFeature[],
  currentBranch?: string,
): TagResult {
  if (features.length === 0) {
    return { confidence: 0, source: "none", status: "untagged" };
  }

  // Accumulate scores per feature
  const scores: Map<string, { score: number; source: TagResult["source"] }> = new Map();

  for (const feature of features) {
    let bestScore = 0;
    let bestSource: TagResult["source"] = "none";

    // Signal 1: Explicit feature ID in conversation (99% confidence)
    const conversationLower = session.conversationText.toLowerCase();
    if (
      conversationLower.includes(feature.id) ||
      conversationLower.includes(feature.name.toLowerCase())
    ) {
      // Check for explicit references like "working on <feature>"
      const explicitPatterns = [
        `feature: ${feature.id}`,
        `feature:${feature.id}`,
        `working on ${feature.id}`,
        `working on ${feature.name.toLowerCase()}`,
        `[${feature.id}]`,
      ];
      if (explicitPatterns.some((p) => conversationLower.includes(p))) {
        bestScore = 0.99;
        bestSource = "explicit";
      }
    }

    // Signal 2: Git branch match (85% confidence)
    if (currentBranch && bestScore < 0.85) {
      const branchLower = currentBranch.toLowerCase();
      if (feature.git_branch_patterns.some((pattern) => matchGlob(branchLower, pattern))) {
        bestScore = Math.max(bestScore, 0.85);
        bestSource = "branch";
      }
    }

    // Signal 3: Keyword match (up to 75% confidence)
    if (bestScore < 0.75) {
      const matchedKeywords = feature.keywords.filter((kw) => conversationLower.includes(kw));
      if (matchedKeywords.length > 0) {
        const keywordScore = Math.min(0.75, 0.4 + matchedKeywords.length * 0.1);
        if (keywordScore > bestScore) {
          bestScore = keywordScore;
          bestSource = "keyword";
        }
      }
    }

    // Signal 4: File path match (up to 70% confidence)
    if (bestScore < 0.7) {
      const matchedFiles = session.filesTouched.filter((f) =>
        feature.file_patterns.some((pattern) => matchGlob(f, pattern)),
      );
      if (matchedFiles.length > 0) {
        const fileScore = Math.min(0.7, 0.35 + matchedFiles.length * 0.1);
        if (fileScore > bestScore) {
          bestScore = fileScore;
          bestSource = "filepath";
        }
      }
    }

    // Compound: multiple signals boost confidence
    let compoundBonus = 0;
    if (currentBranch) {
      const branchMatch = feature.git_branch_patterns.some((p) =>
        matchGlob(currentBranch.toLowerCase(), p),
      );
      const keywordMatch = feature.keywords.some((kw) => conversationLower.includes(kw));
      if (branchMatch && keywordMatch) compoundBonus = 0.1;
    }

    scores.set(feature.id, {
      score: Math.min(1, bestScore + compoundBonus),
      source: bestSource,
    });
  }

  // Pick the highest-scoring feature
  let bestFeatureId: string | undefined;
  let bestEntry = { score: 0, source: "none" as TagResult["source"] };

  for (const [featureId, entry] of scores) {
    if (entry.score > bestEntry.score) {
      bestFeatureId = featureId;
      bestEntry = entry;
    }
  }

  // Apply confidence thresholds
  if (bestEntry.score > 0.7) {
    return {
      featureId: bestFeatureId,
      confidence: bestEntry.score,
      source: bestEntry.source,
      status: "auto-tagged",
    };
  } else if (bestEntry.score >= 0.5) {
    return {
      featureId: bestFeatureId,
      confidence: bestEntry.score,
      source: bestEntry.source,
      status: "likely",
    };
  }

  return { confidence: bestEntry.score, source: "none", status: "untagged" };
}

/**
 * Simple glob matching for branch and file patterns.
 * Supports * as wildcard.
 */
function matchGlob(text: string, pattern: string): boolean {
  const regex = new RegExp(
    "^" + pattern.toLowerCase().replace(/\*/g, ".*").replace(/\?/g, ".") + "$",
  );
  return regex.test(text.toLowerCase());
}
